import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { securityUtils } from '../utils/security';
const WEB3FORMS_ACCESS_KEY = '5234359a-65b7-4f49-a83c-ff4631fe4e3c';
interface LeadCaptureSectionProps {
  language: 'en' | 'es';
}
export function LeadCaptureSection({
  language
}: LeadCaptureSectionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    insuranceType: '',
    carBrand: '',
    carModel: '',
    website_url: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === 'service') {
      setFormData(prev => ({
        ...prev,
        service: value,
        insuranceType: '',
        carBrand: '',
        carModel: ''
      }));
    } else if (name === 'carBrand') {
      setFormData(prev => ({
        ...prev,
        carBrand: value,
        carModel: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    if (error) setError(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // 1. Honeypot Check
    if (formData.website_url !== '') {
      setIsSuccess(true);
      return;
    }
    // 2. Rate Limiting
    if (!securityUtils.rateLimiter.checkLimit(3, 60000)) {
      setError(language === 'en' ? 'Too many attempts. Please try again later.' : 'Demasiados intentos. Por favor, inténtelo más tarde.');
      return;
    }
    // 3. Sanitization
    const sanitizedName = securityUtils.sanitizeInput(formData.name, 100);
    const sanitizedPhone = securityUtils.sanitizeInput(formData.phone, 20);
    const sanitizedEmail = securityUtils.sanitizeInput(formData.email, 100);
    // 4. Validation
    if (!sanitizedName.trim()) {
      setError(language === 'en' ? 'Name is required.' : 'El nombre es obligatorio.');
      return;
    }
    if (!securityUtils.isValidEmail(sanitizedEmail)) {
      setError(language === 'en' ? 'Invalid email format.' : 'Formato de correo inválido.');
      return;
    }
    if (!securityUtils.isValidPhone(sanitizedPhone)) {
      setError(language === 'en' ? 'Invalid phone format.' : 'Formato de teléfono inválido.');
      return;
    }
    if (!formData.service) {
      setError(language === 'en' ? 'Please select a service.' : 'Por favor seleccione un servicio.');
      return;
    }
    if (formData.service === 'insurance' && !formData.insuranceType) {
      setError(language === 'en' ? 'Please select an insurance type.' : 'Por favor seleccione un tipo de seguro.');
      return;
    }
    if (formData.service === 'auto') {
      if (!formData.carBrand) {
        setError(language === 'en' ? 'Please select a car brand.' : 'Por favor seleccione una marca de auto.');
        return;
      }
      if (!formData.carModel) {
        setError(language === 'en' ? 'Please select a car model.' : 'Por favor seleccione un modelo de auto.');
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `🔔 Nuevo Lead - MainPoint Multiservices`,
          from_name: 'MainPoint Website',
          name: sanitizedName,
          email: sanitizedEmail,
          phone: sanitizedPhone,
          service: formData.service,
          ...(formData.service === 'insurance' && {
            insurance_type: formData.insuranceType
          }),
          ...(formData.service === 'auto' && {
            car_brand: formData.carBrand,
            car_model: formData.carModel
          })
        })
      });
      const result = await response.json();
      if (result.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          insuranceType: '',
          carBrand: '',
          carModel: '',
          website_url: ''
        });
      } else {
        setError(language === 'en' ? 'Something went wrong. Please try again.' : 'Algo salió mal. Por favor, inténtelo de nuevo.');
      }
    } catch {
      setError(language === 'en' ? 'Connection error. Please try again.' : 'Error de conexión. Por favor, inténtelo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="py-24 bg-luxury-alt relative" data-id="element-141">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-142">
        <div className="bg-luxury-card rounded-2xl border border-gold-dark/20 p-8 md:p-16 relative overflow-hidden shadow-gold-glow" data-id="element-143">
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t border-l border-gold-primary/40 rounded-tl-2xl" data-id="element-144" />
          <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-gold-primary/40 rounded-tr-2xl" data-id="element-145" />
          <div className="absolute bottom-0 left-0 w-16 h-16 border-b border-l border-gold-primary/40 rounded-bl-2xl" data-id="element-146" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b border-r border-gold-primary/40 rounded-br-2xl" data-id="element-147" />

          <div className="text-center mb-12 relative z-10" data-id="element-148">
            <div className="flex items-center justify-center gap-4 mb-6" data-id="element-149">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold-primary/60" data-id="element-150" />
              <span className="text-gold-light text-xs font-bold tracking-[0.3em] uppercase" data-id="element-151">
                {language === 'en' ? 'Free Consultation' : 'Consulta Gratuita'}
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold-primary/60" data-id="element-152" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4" data-id="element-153">
              {language === 'en' ? 'REQUEST FREE CONSULTATION' : 'SOLICITAR CONSULTA GRATUITA'}
            </h2>
            <p className="text-warm-gray" data-id="element-154">
              {language === 'en' ? 'Take the first step towards securing your financial legacy.' : 'Dé el primer paso para asegurar su legado financiero.'}
            </p>
          </div>

          {error && <div className="mb-6 p-4 bg-brand-red/10 border border-brand-red/30 rounded text-brand-red text-center text-sm font-medium animate-fade-in" data-id="element-155">
              {error}
            </div>}

          {isSuccess ? <div className="text-center py-12 animate-fade-in" data-id="element-156">
              <div className="w-20 h-20 mx-auto bg-gold-primary/10 rounded-full flex items-center justify-center border border-gold-primary/30 mb-6" data-id="element-157">
                <span className="text-gold-light text-3xl" data-id="element-158">✓</span>
              </div>
              <h3 className="font-serif text-2xl text-warm-white mb-2" data-id="element-159">
                {language === 'en' ? 'Request Received' : 'Solicitud Recibida'}
              </h3>
              <p className="text-warm-gray" data-id="element-160">
                {language === 'en' ? 'Our senior advisors will contact you shortly.' : 'Nuestros asesores senior se comunicarán con usted en breve.'}
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-6 relative z-10" noValidate data-id="element-161">
              {/* Honeypot */}
              <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true" data-id="element-162">
                <label htmlFor="website_url" data-id="element-163">Website URL</label>
                <input type="text" id="website_url" name="website_url" tabIndex={-1} autoComplete="off" value={formData.website_url} onChange={handleInputChange} data-id="element-164" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-165">
                <div className="space-y-2" data-id="element-166">
                  <label htmlFor="name" className="text-xs font-bold text-warm-gray uppercase tracking-widest" data-id="element-167">
                    {language === 'en' ? 'Full Name' : 'Nombre Completo'}
                    <span className="text-brand-red ml-1" data-id="element-168">*</span>
                  </label>
                  <input id="name" name="name" type="text" required maxLength={100} autoComplete="name" value={formData.name} onChange={handleInputChange} className="w-full bg-luxury-bg border border-gold-dark/30 rounded px-4 py-3 text-warm-white focus:outline-none focus:border-gold-primary focus:ring-1 focus:ring-gold-primary transition-colors" data-id="element-169" />
                </div>
                <div className="space-y-2" data-id="element-170">
                  <label htmlFor="phone" className="text-xs font-bold text-warm-gray uppercase tracking-widest" data-id="element-171">
                    {language === 'en' ? 'Phone Number' : 'Teléfono'}
                    <span className="text-brand-red ml-1" data-id="element-172">*</span>
                  </label>
                  <input id="phone" name="phone" type="tel" required maxLength={20} autoComplete="tel" value={formData.phone} onChange={handleInputChange} className="w-full bg-luxury-bg border border-gold-dark/30 rounded px-4 py-3 text-warm-white focus:outline-none focus:border-gold-primary focus:ring-1 focus:ring-gold-primary transition-colors" data-id="element-173" />
                </div>
              </div>

              <div className="space-y-2" data-id="element-174">
                <label htmlFor="email" className="text-xs font-bold text-warm-gray uppercase tracking-widest" data-id="element-175">
                  {language === 'en' ? 'Email Address' : 'Correo Electrónico'}
                  <span className="text-brand-red ml-1" data-id="element-176">*</span>
                </label>
                <input id="email" name="email" type="email" required maxLength={100} autoComplete="email" value={formData.email} onChange={handleInputChange} className="w-full bg-luxury-bg border border-gold-dark/30 rounded px-4 py-3 text-warm-white focus:outline-none focus:border-gold-primary focus:ring-1 focus:ring-gold-primary transition-colors" data-id="element-177" />
              </div>

              <div className="space-y-2" data-id="element-178">
                <label htmlFor="service" className="text-xs font-bold text-warm-gray uppercase tracking-widest" data-id="element-179">
                  {language === 'en' ? 'Service of Interest' : 'Servicio de Interés'}
                  <span className="text-brand-red ml-1" data-id="element-180">*</span>
                </label>
                <select id="service" name="service" value={formData.service} onChange={handleInputChange} required className="w-full bg-luxury-bg border border-gold-dark/30 rounded px-4 py-3 text-warm-white focus:outline-none focus:border-gold-primary focus:ring-1 focus:ring-gold-primary transition-colors appearance-none" data-id="element-181">
                  <option value="" data-id="element-182">
                    {language === 'en' ? 'Select a service...' : 'Seleccione un servicio...'}
                  </option>
                  <option value="auto" data-id="element-183">
                    {language === 'en' ? 'Vehicle' : 'Vehículo'}
                  </option>
                  <option value="tax" data-id="element-184">
                    {language === 'en' ? 'Tax Planning' : 'Planificación Fiscal'}
                  </option>
                  <option value="insurance" data-id="element-185">
                    {language === 'en' ? 'Insurance Solutions' : 'Soluciones de Seguros'}
                  </option>
                  <option value="both" data-id="element-186">
                    {language === 'en' ? 'Comprehensive Portfolio' : 'Portafolio Integral'}
                  </option>
                </select>

                <AnimatePresence data-id="element-187">
                  {formData.service === 'insurance' && <motion.div initial={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} animate={{
                opacity: 1,
                height: 'auto',
                marginTop: 16
              }} exit={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} className="space-y-2 overflow-hidden" data-id="element-188">
                      <label htmlFor="insuranceType" className="text-xs font-bold text-warm-gray uppercase tracking-widest" data-id="element-189">
                        {language === 'en' ? 'Insurance Type' : 'Tipo de Seguro'}
                        <span className="text-brand-red ml-1" data-id="element-190">*</span>
                      </label>
                      <select id="insuranceType" name="insuranceType" value={formData.insuranceType} onChange={handleInputChange} required className="w-full bg-luxury-bg border border-gold-dark/30 rounded px-4 py-3 text-warm-white focus:outline-none focus:border-gold-primary focus:ring-1 focus:ring-gold-primary transition-colors appearance-none" data-id="element-191">
                        <option value="" data-id="element-192">
                          {language === 'en' ? 'Select insurance type...' : 'Seleccione tipo de seguro...'}
                        </option>
                        <option value="auto" data-id="element-193">
                          {language === 'en' ? 'Auto Insurance' : 'Seguro de Auto'}
                        </option>
                        <option value="health" data-id="element-194">
                          {language === 'en' ? 'Health Insurance' : 'Seguro de Salud'}
                        </option>
                        <option value="life" data-id="element-195">
                          {language === 'en' ? 'Life Insurance' : 'Seguro de Vida'}
                        </option>
                      </select>
                    </motion.div>}

                  {formData.service === 'auto' && <motion.div initial={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} animate={{
                opacity: 1,
                height: 'auto',
                marginTop: 16
              }} exit={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} className="space-y-4 overflow-hidden" data-id="element-196">
                      <div className="space-y-2" data-id="element-197">
                        <label htmlFor="carBrand" className="text-xs font-bold text-warm-gray uppercase tracking-widest" data-id="element-198">
                          {language === 'en' ? 'Car Brand' : 'Marca de Auto'}
                          <span className="text-brand-red ml-1" data-id="element-199">*</span>
                        </label>
                        <select id="carBrand" name="carBrand" value={formData.carBrand} onChange={handleInputChange} required className="w-full bg-luxury-bg border border-gold-dark/30 rounded px-4 py-3 text-warm-white focus:outline-none focus:border-gold-primary focus:ring-1 focus:ring-gold-primary transition-colors appearance-none" data-id="element-200">
                          <option value="" data-id="element-201">
                            {language === 'en' ? 'Select a brand...' : 'Seleccione una marca...'}
                          </option>
                          <option value="KIA" data-id="element-202">KIA</option>
                          <option value="TOYOTA" data-id="element-203">TOYOTA</option>
                        </select>
                      </div>

                      <AnimatePresence data-id="element-204">
                        {formData.carBrand && <motion.div initial={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto',
                    marginTop: 16
                  }} exit={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} className="space-y-2 overflow-hidden" data-id="element-205">
                            <label htmlFor="carModel" className="text-xs font-bold text-warm-gray uppercase tracking-widest" data-id="element-206">
                              {language === 'en' ? 'Car Model' : 'Modelo de Auto'}
                              <span className="text-brand-red ml-1" data-id="element-207">*</span>
                            </label>
                            <select id="carModel" name="carModel" value={formData.carModel} onChange={handleInputChange} required className="w-full bg-luxury-bg border border-gold-dark/30 rounded px-4 py-3 text-warm-white focus:outline-none focus:border-gold-primary focus:ring-1 focus:ring-gold-primary transition-colors appearance-none" data-id="element-208">
                              <option value="" data-id="element-209">
                                {language === 'en' ? 'Select a model...' : 'Seleccione un modelo...'}
                              </option>
                              {formData.carBrand === 'KIA' && <>
                                  <option value="Sorento" data-id="element-210">Sorento</option>
                                  <option value="Telluride" data-id="element-211">Telluride</option>
                                  <option value="Sportage" data-id="element-212">Sportage</option>
                                  <option value="K5" data-id="element-213">K5</option>
                                  <option value="Soul" data-id="element-214">Soul</option>
                                  <option value="Carnival" data-id="element-215">Carnival</option>
                                </>}
                              {formData.carBrand === 'TOYOTA' && <>
                                  <option value="Camry" data-id="element-216">Camry</option>
                                  <option value="Corolla" data-id="element-217">Corolla</option>
                                  <option value="RAV4" data-id="element-218">RAV4</option>
                                  <option value="Highlander" data-id="element-219">Highlander</option>
                                  <option value="Tacoma" data-id="element-220">Tacoma</option>
                                  <option value="Sienna" data-id="element-221">Sienna</option>
                                </>}
                            </select>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div className="pt-6 border-t border-gold-dark/10" data-id="element-222">
                <button type="submit" disabled={isSubmitting} className="w-full bg-brand-red text-white font-bold uppercase tracking-widest py-4 rounded hover:bg-red-700 hover:shadow-red-glow transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-223">
                  {isSubmitting ? language === 'en' ? 'Processing...' : 'Procesando...' : language === 'en' ? 'Submit Request' : 'Enviar Solicitud'}
                </button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}